import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  LinearProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  Chip,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { apiService } from '../../../services/apiService';
import { describeFilter, proposeTargetFilter, collectFilterCodes } from '../../../utils/filterMapping';

/**
 * Map a server-parsed target (snake_case, decimal-degree coords) to the
 * observation form's target shape (camelCase). Coordinates are kept as
 * strings because the RA/Dec inputs accept decimal-degrees or sexagesimal text.
 */
function toFormTarget(t) {
  return {
    name: t.name || '',
    ra: t.ra != null ? String(t.ra) : '',
    dec: t.dec != null ? String(t.dec) : '',
    priority: t.priority ?? 5,
    sequenceRepeatCount: t.sequence_repeat_count || 1,
    exposureConfigs: (t.exposure_configs || []).map((ec) => ({
      filter: ec.filter,
      exposureTime: ec.exposure_time,
      exposureCount: ec.exposure_count,
      binning: ec.binning != null ? String(ec.binning) : '1',
      gain: ec.gain ?? null,
      offset: ec.offset ?? null,
    })),
  };
}

/** Apply a {sourceCode -> observatoryFilter} map to a target's exposure configs. */
function applyFilterMap(target, filterMap) {
  return {
    ...target,
    exposureConfigs: (target.exposureConfigs || []).map((ec) => ({
      ...ec,
      filter: filterMap[ec.filter] || ec.filter,
    })),
  };
}

/**
 * Parses a CSV target list (client-side via the csv-import preview endpoint),
 * lets the user reconcile the source's filter names against the target
 * observatory's installed filters, then hands the mapped targets to the parent
 * to append to the form.
 */
const TargetCsvImportDialog = ({ open, onClose, onImport, availableFilters = [] }) => {
  const [formats, setFormats] = useState([]);
  const [formatType, setFormatType] = useState('generic');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // phase: 'select' (pick file) → 'map' (reconcile filters) → 'done'
  const [phase, setPhase] = useState('select');
  const [parsedTargets, setParsedTargets] = useState([]);
  const [filterCodes, setFilterCodes] = useState([]);
  const [filterMap, setFilterMap] = useState({});
  const [skipped, setSkipped] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    apiService
      .getCsvImportFormats()
      .then((d) => setFormats(d.data || []))
      .catch(() => {/* format list is optional; default 'generic' still works */});
  }, [open]);

  const reset = () => {
    setFile(null);
    setError('');
    setLoading(false);
    setPhase('select');
    setParsedTargets([]);
    setFilterCodes([]);
    setFilterMap({});
    setSkipped(0);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileSelect = (event) => {
    const selected = event.target.files[0];
    if (selected) {
      setFile(selected);
      setError('');
    }
  };

  // Phase 1 → 2: parse the file and build the proposed filter mapping.
  const handleParse = async () => {
    if (!file) return;
    setLoading(true);
    setError('');
    try {
      const resp = await apiService.previewCsvImport(file, formatType, 1000);
      const rows = resp.data?.preview || [];
      const targets = rows.filter((o) => !o.error && o.targets?.[0]).map((o) => toFormTarget(o.targets[0]));
      if (targets.length === 0) {
        setError('No valid targets found. Check that the selected format matches the file and that it has name/RA/Dec columns.');
        setLoading(false);
        return;
      }

      const codes = collectFilterCodes(targets);
      const proposed = {};
      codes.forEach((code) => {
        proposed[code] = proposeTargetFilter(code, availableFilters);
      });

      setParsedTargets(targets);
      setFilterCodes(codes);
      setFilterMap(proposed);
      setSkipped(resp.data?.parse_errors?.length || 0);

      // Nothing to map (no filters in file, or observatory has no filter list) → import straight away.
      if (codes.length === 0 || availableFilters.length === 0) {
        onImport(targets);
        setPhase('done');
      } else {
        setPhase('map');
      }
    } catch (err) {
      const apiErr = err.response?.data?.error;
      setError((typeof apiErr === 'string' ? apiErr : apiErr?.message) || 'Failed to parse CSV file');
    } finally {
      setLoading(false);
    }
  };

  // Phase 2 → done: apply the mapping and hand targets to the form.
  const handleConfirmImport = () => {
    const mapped = parsedTargets.map((t) => applyFilterMap(t, filterMap));
    onImport(mapped);
    setPhase('done');
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await apiService.downloadCsvTemplate(formatType);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${formatType}_template.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      setError('Failed to download template');
    }
  };

  const unmappedCount = filterCodes.filter((c) => !filterMap[c]).length;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import Target List from CSV</DialogTitle>
      <DialogContent>
        {phase === 'select' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Import targets from a CSV file into this observation. Imported targets are
              added to the list below — you can keep adding or editing targets afterwards.
            </Typography>

            <FormControl fullWidth margin="normal" size="small">
              <InputLabel>CSV Format</InputLabel>
              <Select value={formatType} label="CSV Format" onChange={(e) => setFormatType(e.target.value)}>
                {(formats.length ? formats : [{ id: 'generic', name: 'Generic CSV' }]).map((f) => (
                  <MenuItem key={f.id} value={f.id}>
                    {f.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button size="small" startIcon={<DownloadIcon />} onClick={handleDownloadTemplate} sx={{ mt: 1 }}>
              Download {formatType} template
            </Button>

            <Box sx={{ border: '2px dashed', borderColor: 'divider', borderRadius: 2, p: 3, mt: 2, textAlign: 'center' }}>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".csv,.txt"
                style={{ display: 'none' }}
              />
              {file ? (
                <Typography variant="body1">Selected: {file.name}</Typography>
              ) : (
                <Typography variant="body1" color="text.secondary">
                  No file selected
                </Typography>
              )}
              <Button
                variant="contained"
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ mt: 2 }}
              >
                Select CSV File
              </Button>
            </Box>

            {loading && <LinearProgress sx={{ mt: 2 }} />}
            {error && (
              <Alert severity="error" sx={{ mt: 2, whiteSpace: 'pre-line' }}>
                {error}
              </Alert>
            )}
          </>
        )}

        {phase === 'map' && (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {parsedTargets.length} target{parsedTargets.length === 1 ? '' : 's'} parsed. Map each filter from the
              CSV ({formatType}) to a filter installed on the selected observatory. Hover a CSV
              filter to see its full name; defaults are the best-guess match.
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>CSV Filter</TableCell>
                    <TableCell>Observatory Filter</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filterCodes.map((code) => {
                    const fullName = describeFilter(formatType, code);
                    const unmapped = !filterMap[code];
                    return (
                      <TableRow key={code}>
                        <TableCell>
                          <Tooltip title={fullName && fullName !== code ? fullName : 'No standard name'} arrow>
                            <Chip
                              label={code}
                              size="small"
                              variant="outlined"
                              color={unmapped ? 'warning' : 'default'}
                              icon={unmapped ? <WarningAmberIcon /> : undefined}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" fullWidth error={unmapped}>
                            <Select
                              value={filterMap[code] || ''}
                              displayEmpty
                              onChange={(e) => setFilterMap((prev) => ({ ...prev, [code]: e.target.value }))}
                            >
                              <MenuItem value="">
                                <em>— select filter —</em>
                              </MenuItem>
                              {availableFilters.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {opt.label || opt.value}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {unmappedCount > 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {unmappedCount} filter{unmappedCount === 1 ? '' : 's'} still need a target filter selected.
              </Alert>
            )}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </>
        )}

        {phase === 'done' && (
          <Alert severity={skipped > 0 ? 'warning' : 'success'} sx={{ mt: 1 }}>
            Added {parsedTargets.length} target{parsedTargets.length === 1 ? '' : 's'}
            {skipped > 0 ? ` (${skipped} row${skipped === 1 ? '' : 's'} skipped during parsing)` : ''}.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        {phase === 'select' && (
          <>
            <Button onClick={handleClose}>Cancel</Button>
            <Button variant="contained" onClick={handleParse} disabled={!file || loading}>
              Next: Map Filters
            </Button>
          </>
        )}
        {phase === 'map' && (
          <>
            <Button onClick={() => setPhase('select')}>Back</Button>
            <Button variant="contained" onClick={handleConfirmImport} disabled={unmappedCount > 0}>
              Import {parsedTargets.length} Target{parsedTargets.length === 1 ? '' : 's'}
            </Button>
          </>
        )}
        {phase === 'done' && (
          <Button variant="contained" onClick={handleClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TargetCsvImportDialog;
